import React, { useState, useEffect } from "react";
import { Button, Form, Input, Picker, Toast, NavBar } from 'antd-mobile';
import { useNavigate, useLocation } from 'react-router-dom';
import type { PickerValue } from "antd-mobile/es/components/picker";
import { t } from "../../../../i18n";
import { getNavBarContainerStyle, isIOS, isAndroid } from "../../../../utils/deviceUtils";
import { loginWithVerificationCode, useHandleLoginSuccess } from "../../../../utils/auth";
import { getAppVersion } from "../../../../utils/appRuntime";
import { useSendVerificationCode } from '../../../../hooks/useSendVerificationCode';
import { validatePhoneNumber } from '../../../../utils/phoneValidation';
import { validateVerificationCodeFormat } from '../../../../utils/verificationCodeValidation';

const countryCodes = [
    { label: '+1', value: '+1', flag: '🇺🇸' },
];

const BlacklistSmsVerificationPage: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const handleLoginSuccess = useHandleLoginSuccess();
    const [verificationCode, setVerificationCode] = useState('');
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCode, setSelectedCode] = useState<string>('+1');
    
    // 从location.state获取登录时输入的账号信息
    const [loginAccount, setLoginAccount] = useState<{
        countryCode: string;
        phoneOrEmail: string;
    } | null>(null);

    // 获取验证码倒计时
    const { sendCode: sendLoginVerificationCode, countdown } = useSendVerificationCode({
        type: 'login',
        getPhone: () => ({
            phone: form.getFieldValue('phoneOrEmail'),
            countryCode: form.getFieldValue('countryCode') || selectedCode
        }),
        onDisabled: () => Toast.show(t('errors.business.account.disabled')),
    });

    // 从location.state获取登录账号信息
    useEffect(() => {
        if (location.state) {
            const { countryCode, phoneOrEmail } = location.state as any;
            if (countryCode && phoneOrEmail) {
                setLoginAccount({ countryCode, phoneOrEmail });
                // 设置表单初始值
                form.setFieldsValue({
                    countryCode: countryCode || '+1',
                    phoneOrEmail: ''
                });
                setSelectedCode(countryCode || '+1');
            } else {
                // 如果没有传递账号信息，返回登录页
                navigate('/login');
            }
        } else {
            // 如果没有state，返回登录页
            navigate('/login');
        }
    }, [location.state, form, navigate]);

    // 处理验证码登录
    const onVerificationCodeLogin = async (values: { countryCode: string; phoneOrEmail: string }) => {
        try {
            // 1. 校验手机号格式
            const phoneValidation = validatePhoneNumber(values.phoneOrEmail, values.countryCode);
            if (!phoneValidation.isValid) {
                Toast.show(phoneValidation.errorMessage!);
                return;
            }

            // 2. 判断输入的手机号是否与登录账号一致
            if (loginAccount) {
                // 将登录账号的phoneOrEmail转换为纯数字进行比较
                const loginPhoneDigits = loginAccount.phoneOrEmail.replace(/\D/g, '');
                const inputPhoneDigits = phoneValidation.phoneDigits!;
                
                // 比较国家区号（转换为数字）
                const loginCountryCode = Number(loginAccount.countryCode.replace('+', ''));
                const inputCountryCode = Number(values.countryCode.replace('+', ''));
                
                if (loginPhoneDigits !== inputPhoneDigits || loginCountryCode !== inputCountryCode) {
                    // 手机号不一致，显示错误提示
                    Toast.show(t('pages.login.blacklistSmsVerification.phoneMismatch'));
                    return;
                }
            }

            // 3. 校验验证码格式
            const codeValidation = validateVerificationCodeFormat(verificationCode);
            if (!codeValidation.isValid) {
                Toast.show(codeValidation.errorMessage!);
                return;
            }

            // 4. 调用验证码登录API（后端会再次校验验证码，并将account表的is_blacklist字段从1更新为0）
            const platform = isIOS() ? 'iOS' : isAndroid() ? 'Android' : 'Web';
            const versionName = await getAppVersion();
            const result = await loginWithVerificationCode(
                Number(values.countryCode.replace('+', '')),
                phoneValidation.phoneDigits!,
                verificationCode.trim(),
                undefined, // deviceId
                true, // removeBlacklist: true，后端会将account表的is_blacklist字段从1更新为0
                platform,
                versionName
            );

            if (result.userInfo) {
                handleLoginSuccess(result.userInfo);
            } else {
                // 处理业务错误
                const messageKey = result.messageKey;
                if (messageKey === 'errors.validation.invalidPhoneNumber') {
                    Toast.show(t('errors.validation.invalidPhoneNumber'));
                } else if (messageKey === 'errors.business.account.verificationCode.invalid') {
                    Toast.show(t('common.verificationCode.invalid'));
                } else if (messageKey === 'errors.business.account.verificationCode.notAllowedForRole') {
                    Toast.show(t('errors.business.account.verificationCode.notAllowedForRole'));
                } else if (messageKey === 'errors.business.account.disabled') {
                    Toast.show(t('errors.business.account.disabled'));
                } else {
                    Toast.show(t('errors.login.failed'));
                }
            }
        } catch (error: any) {
            // 处理真正的异常（网络错误等）
            Toast.show(t('errors.login.failed'));
        }
    };


    // 获取验证码
    const handleGetVerificationCode = async () => {
        // 获取当前表单中的手机号和区号
        const currentPhone = form.getFieldValue('phoneOrEmail');
        const currentCountryCode = form.getFieldValue('countryCode') || selectedCode;

        // 1. 校验手机号格式
        const phoneValidation = validatePhoneNumber(currentPhone, currentCountryCode);
        if (!phoneValidation.isValid) {
            Toast.show(phoneValidation.errorMessage!);
            return;
        }

        // 2. 判断输入的手机号是否与登录账号一致
        if (loginAccount) {
            // 将登录账号的phoneOrEmail转换为纯数字进行比较
            const loginPhoneDigits = loginAccount.phoneOrEmail.replace(/\D/g, '');
            const inputPhoneDigits = phoneValidation.phoneDigits!;
            
            // 比较国家区号（转换为数字）
            const loginCountryCode = Number(loginAccount.countryCode.replace('+', ''));
            const inputCountryCode = Number(currentCountryCode.replace('+', ''));
            
            if (loginPhoneDigits !== inputPhoneDigits || loginCountryCode !== inputCountryCode) {
                // 手机号不一致，显示错误提示
                Toast.show(t('pages.login.blacklistSmsVerification.phoneMismatch'));
                return;
            }
        }

        // 3. 手机号校验通过，发送验证码
        await sendLoginVerificationCode();
    };

    const onFinish = async (values: any) => {
        await onVerificationCodeLogin(values);
    };

    return (
        <div className="min-h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => navigate('/login')}
                >
                    {t('pages.login.blacklistSmsVerification.title')}
                </NavBar>
            </div>
            <div className="flex flex-col p-4 gap-8">
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{ countryCode: '+1' }}
                    layout="vertical"
                    style={{
                        '--border-top': 'none',
                        '--border-inner': 'none',
                        '--border-bottom': 'none',
                    }}
                >
                    {/*手机号标签*/}
                    <div className="text-lg font-semibold mb-2">
                        {t('pages.login.blacklistSmsVerification.phoneNumberLabel')}
                        <span className="text-red-500">*</span>
                    </div>

                    {/*区号&&电话号*/}
                    <div className="flex gap-2 mb-4">
                        <Form.Item name="countryCode" className="mb-0 w-[70px]">
                            <div
                                className="w-full h-full flex items-center justify-center gap-1 cursor-pointer"
                                onClick={() => setPickerVisible(true)}
                            >
                                <span className="text-lg">
                                    {countryCodes.find(c => c.value === selectedCode)?.flag || '🇺🇸'}
                                </span>
                                <span>{selectedCode}</span>
                            </div>
                        </Form.Item>

                        <Form.Item 
                            name="phoneOrEmail" 
                            className="mb-0 flex-1"
                        >
                            <Input
                                clearable
                                placeholder={t('pages.login.phoneNumber')}
                                type="tel"
                                inputMode="tel"
                                value={form.getFieldValue('phoneOrEmail') || ''}
                                onChange={val => {
                                    // 只允许数字，限制长度（中国11位，美国10位）
                                    const digitsOnly = val.replace(/\D/g, '');
                                    const maxLength = selectedCode === '+86' ? 11 : 10;
                                    form.setFieldValue('phoneOrEmail', digitsOnly.slice(0, maxLength));
                                }}
                            />
                        </Form.Item>
                    </div>

                    <Picker
                        columns={[countryCodes.map(code => ({
                            label: `${code.flag} ${code.label}`,
                            value: code.value
                        }))]}
                        visible={pickerVisible}
                        value={[selectedCode]}
                        onClose={() => setPickerVisible(false)}
                        onConfirm={(val: PickerValue[]) => {
                            const newCode = String(val[0]);
                            setSelectedCode(newCode);
                            form.setFieldsValue({ countryCode: newCode });
                            setPickerVisible(false);
                        }}
                    />

                    {/*验证码标签*/}
                    <div className="text-lg font-semibold mb-2">
                        {t('pages.login.blacklistSmsVerification.verificationCodeLabel')}
                        <span className="text-red-500">*</span>
                    </div>

                    {/*验证码输入*/}
                    <Form.Item className="w-full mb-4">
                        <div className="adm-input">
                            <Input
                                className="adm-input-element"
                                placeholder={t('common.verificationCode.placeholder')}
                                type="tel"
                                inputMode="numeric"
                                value={verificationCode}
                                onChange={val => {
                                    const digitsOnly = val.replace(/\D/g, '').slice(0, 6);
                                    setVerificationCode(digitsOnly);
                                }}
                            />
                            <div className="px-2 whitespace-nowrap flex-shrink-0">
                                <Button
                                    size="small"
                                    color="primary"
                                    fill="none"
                                    disabled={countdown > 0}
                                    onClick={handleGetVerificationCode}
                                >
                                    {countdown > 0 ? `${countdown}s` : t('pages.login.getCode')}
                                </Button>
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item>
                        <div className="flex justify-center">
                            <Button
                                block
                                color="primary"
                                size="large"
                                type="submit"
                            >
                                {t('pages.login.blacklistSmsVerification.ok')}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>

            </div>
        </div>
    );
};

export default BlacklistSmsVerificationPage;

